import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import { personAPI } from 'api/PersonAPI'
import { ImageDropzone } from 'Components/reusable/InputFields/Dropzone/ImageDropzone'
import DeleteButton from 'Components/reusable/IconButtons/DeleteButton'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/src/ReactCrop.scss'
import { IError } from 'types/error'
import { getCroppedImg } from 'utils/utils'

/** @notExported */
interface IProfileImageEditorProps {
  /** Person ID. */
  personId: number
  /** Current profile image. */
  oldImage: string
  /** Called after the current profile image has been deleted. */
  changeOldImage: (image: string) => void
  /** Called with the cropped new image. The caller uploads it when saving. */
  onCroppedImageChange: (image: File) => void
  /** Called when deleting the current image fails. */
  onError: (error: IError) => void
}

/**
 * Profile image drop zone with cropping and deleting of the current image.
 *
 * @returns Profile image editor component.
 * @notExported
 */
const ProfileImageEditor: React.FC<IProfileImageEditorProps> = ({
  personId,
  oldImage,
  changeOldImage,
  onCroppedImageChange,
  onError,
}) => {
  const { t } = useTranslation()
  const [image, setImage] = useState<HTMLImageElement>()
  const [crop, setCrop] = useState<Crop>({ unit: '%', width: 50, height: 50, x: 25, y: 25 })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const newimage = new Image()
        newimage.src = reader.result
        setImage(newimage)
      }
    }
    reader.readAsDataURL(acceptedFiles[0])
  }, [])

  const onCropComplete = async (crop?: Crop) => {
    if (image) {
      const croppedImageFile = await getCroppedImg(crop ?? { width: 50, height: 50, unit: '%', x: 0, y: 0 }, image)
      if (croppedImageFile instanceof File) {
        onCroppedImageChange(croppedImageFile)
      }
    }
  }

  const deleteProfileImage = async () => {
    try {
      await personAPI.deleteProfileImage(personId)
      changeOldImage('')
    } catch (error) {
      onError(error as IError)
    }
  }

  return (
    <Grid sx={{ display: 'block', width: '100%', height: '100%', marginBottom: '25px' }}>
      {!image && (
        <ImageDropzone
          onDrop={onDrop}
          accept={{ 'image/jpg': ['.jpg'], 'image/png': ['.png'] }}
          multiple={false}
          oldImage={!image ? oldImage : ''}
          dragHint={t('dropbox.dragProfilePicture')}
          dropHint={t('dropbox.dropProfilePicture')}
        />
      )}
      {image && (
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Helvetica',
            width: '100%',
            height: '218px',
            borderRadius: '5px',
            backgroundColor: theme => theme.palette.neutral.light,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Grid
            sx={{
              borderRadius: '5px 5px 0 0',
              display: 'flex',
              height: '100%',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReactCrop
              keepSelection
              aspect={1 / 1}
              crop={crop}
              onChange={newCrop => setCrop(newCrop)}
              ruleOfThirds
              onComplete={(_, value) => onCropComplete(value)}
            >
              <img
                src={image.src}
                alt="cropped image"
                style={{ height: '188px' }}
                onLoad={() => {
                  setCrop({ unit: '%', width: 50, height: 50, x: 0, y: 0 })
                  onCropComplete(crop)
                }}
              />
            </ReactCrop>
          </Grid>
          <Grid
            sx={{
              display: 'flex',
              width: '100%',
              height: '30px',
              alignSelf: 'flex-end',
              backgroundColor: theme => theme.palette.warning.main,
              borderRadius: '0 0 5px 5px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ fontSize: '14px', fontWeight: 'bold', color: theme => theme.palette.background.default }}>
              {t('dropbox.cropInfo')}
            </Box>
          </Grid>
        </Grid>
      )}
      {oldImage && (
        <DeleteButton testId="button-profile-modal-delete-image" clickAction={deleteProfileImage} size="medium" />
      )}
    </Grid>
  )
}

export default ProfileImageEditor
